import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import FeedBackService from '../Services/FeedBackService';
import './FeedBack.css';
import { Rating } from 'react-simple-star-rating';
import SweetAlertService from '../../../services/SweetAlertService';

export const Feedback = () => {
    const [starRating, setStarRating] = useState(0);
    const [comments, setComments] = useState('');
    const [isConsentToContact, setIsCheckboxChecked] = useState(false);
    const [isConsentToMarketing, setIsConsentToMarketing] = useState(false);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const [hovered, setHovered] = useState(0);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
    } = useForm();

    const onSubmit = (data: any) => {
        data.starRating = starRating;
        FeedBackService.addFeedBack(data)
            .then((res: any) => {
                if (res) {
                    setIsConfirmed(true);
                    setComments('');
                } else {
                    SweetAlertService.error("Error", "Something went wrong while submitting the FeedBack");
                }
                reset();
            })
            .catch(() => {
                SweetAlertService.error("Error", "Something went wrong while submitting the FeedBack");
            });
    };

    const handleHoverLeave = () => {
        setHovered(starRating);
    };

    const handleStarChange = (value: number) => {
        setIsConfirmed(false);
        setIsCheckboxChecked(false);
        setIsConsentToMarketing(false);
        setStarRating(value);
        setHovered(value);
        if (isIOS) {
            setTimeout(() => setHovered(value), 50);
        }
    };

    const handleHover = (value: number) => {
        setHovered(value);
    };

    const handleChangeComments = (event: { target: { value: React.SetStateAction<string>; }; }) => {
        setComments(event.target.value);
        if (event.target.value.length <= 0) {
            setIsCheckboxChecked(false);
            setIsConsentToMarketing(false);
        }
    };

    const handleCheckboxChange = (event: { target: { checked: boolean | ((prevState: boolean) => boolean); }; }) => {
        setIsCheckboxChecked(event.target.checked);
    };

    const handleCheckboxChangeForAllowAnonymous = (event: { target: { checked: boolean | ((prevState: boolean) => boolean); }; }) => {
        setIsConsentToMarketing(event.target.checked);
    };

    const renderRatingCaption = () => {
        let caption = '';
        let backgroundColor = '';

        switch (hovered) {
            case 0:
                caption = 'Not Rated';
                backgroundColor = '#777';
                break;
            case 1:
                caption = 'One Star';
                backgroundColor = '#d9534f';
                break;
            case 2:
                caption = 'Two Stars';
                backgroundColor = '#f0ad4e';
                break;
            case 3:
                caption = 'Three Stars';
                backgroundColor = '#5bc0de';
                break;
            case 4:
                caption = 'Four Stars';
                backgroundColor = '#af1478';
                break;
            case 5:
                caption = 'Five Stars';
                backgroundColor = '#5cb85c';
                break;
            default:
                break;
        }

        const style = {
            backgroundColor: backgroundColor,
        };
        return (
            <span className={`label ${'ratingCaption'}`} style={style}>{caption}</span>
        );
    };

    return (
        <div className="card-body">
            <form onSubmit={handleSubmit(onSubmit)}>
                <div className="form-group">
                    <fieldset className="container py-4">
                        <div>
                            <h2>Are you finding GP Diary helpful? Tell us what you think.</h2>
                            <p className="my-2">Your experience matters to us. If you have any feedback on how GP Diary is working for you, we’d love to hear from you.</p>
                            <h2 className="mt-3">Why leave feedback?</h2>
                            <ul className="ps-5">
                                <li>
                                    <strong>Support your colleagues – </strong>Positive ratings guide others to use the tool confidently
                                </li>
                                <li>
                                    <strong>Celebrate what works – </strong>Highlight the features that make your day easier
                                </li>
                                <li>
                                    <strong>Suggestions for things to improve – </strong>these are valuable to us and we’d love to hear them
                                </li>
                            </ul>
                            <h2 className="my-3">How to share</h2>
                            <ul className="ps-5">
                                <li>Use the ratings and comment box below to tell us what you think.</li>
                                <li>Rate your experience and add a quick comment.</li>
                                <li>Share what you love – it only takes a minute!</li>
                                <li>If there is anything you would like us to add to the tool, include them in your comments.</li>
                            </ul>
                            <p className="my-2">Your voice makes GP Diary better. Thank you!</p>

                            <div className="d-inline-flex">
                                <Rating
                                    onClick={handleStarChange}
                                    initialValue={starRating}
                                    transition
                                    allowHover
                                    onPointerLeave={handleHoverLeave}
                                    onPointerMove={handleHover}
                                    fillColor="#d60054"
                                    fillClassName={`glyphicon-star ${starRating >= starRating ? 'filled' : ''}`}
                                    emptyColor="#aaa"
                                    fillIcon={<span>&#9733;</span>}
                                    emptyIcon={<span>&#9734;</span>}
                                    className="display-4"
                                    allowTitleTag={false}
                                />
                                <div className="caption k-display-inline-block mt-4 pt-0 px-1">{renderRatingCaption()}</div>
                            </div>
                        </div>

                        {comments && (
                            <div className="form-group">
                                <div className="mt-4 mb-4">
                                    <span className="text">
                                        <input className="checkbox"
                                            type="checkbox"
                                            {...register('isConsentToContact')}
                                            id="isConsentCheckbox"
                                            checked={isConsentToContact}
                                            onChange={handleCheckboxChange}
                                        />{' '}
                                        I would like to give feedback and be contacted in response.
                                    </span>
                                </div>
                            </div>
                        )}

                        {starRating > 0 && (
                            <div className="mt-3 form-group">
                                <p className="mb-3">Why did you give this rating?</p>
                                <textarea
                                    className="form-control"
                                    rows={8}
                                    {...register('comments')}
                                    onChange={handleChangeComments}
                                    placeholder="Post your feedback here..."
                                ></textarea>
                                {comments.length > 1000 && (
                                    <span className="text-danger">
                                        Maximum 1000 characters
                                    </span>
                                )}
                            </div>
                        )}
                        {comments && (
                            <div className="form-group">
                                <div className="mt-3 mb-2">
                                    <span className="text">
                                        <input className="checkbox"
                                            type="checkbox"
                                            {...register('isConsentToMarketing')}
                                            id="anonymousUseCheckbox"
                                            checked={isConsentToMarketing}
                                            onChange={handleCheckboxChangeForAllowAnonymous}
                                        />{' '}
                                        If you would be happy for us to use your feedback anonymously in our promotion of GP Diary to other GP members, please tick this box.
                                    </span>
                                </div>
                            </div>
                        )}
                        {starRating > 0 && (
                            <div>
                                <button
                                    type='submit'
                                    className="btn btn-primary mt-2"
                                >
                                    Send
                                </button>&nbsp;
                                {isConfirmed && (<span>
                                    <img className="checkmark" src={require('../../../assets/images/checkmark.png')} alt="checkbox" />Thank you for your feedback
                                </span>)}
                            </div>
                        )}
                        <div className="mt-1">
                            <h2 className="mt-3 mb-2">Recommend us</h2>
                            <div className="feedback_footer-social-buttons mt-3 mb-3 mx-3">
                                <a target="_blank" href="http://twitter.com/theBMA" aria-label="Twitter link" className="feedback_footer-social-buttons__button">
                                    <i className="bi bi-twitter-x"></i>
                                </a>

                                <a target="_blank" href="https://www.instagram.com/thebma/?hl=en" aria-label="Instagram link" className="feedback_footer-social-buttons__button">
                                    <i className="bi bi-instagram"></i>
                                </a>
                                <a target="_blank" href="http://www.linkedin.com/company/bma" aria-label="Linkedin link" className="feedback_footer-social-buttons__button">
                                    <i className="bi bi-linkedin"></i>
                                </a>
                                <a target="_blank" href="http://www.youtube.com/bmatv" aria-label="Youtube link" className="feedback_footer-social-buttons__button">
                                    <i className="bi bi-youtube"></i>
                                </a>
                                <a target="_blank" href="http://www.facebook.com/TheBMA" aria-label="Facebook link" className="feedback_footer-social-buttons__button">
                                    <i className="bi bi-facebook"></i>
                                </a>
                            </div>
                        </div>
                    </fieldset>
                </div>
            </form>
        </div>
    );
};